import { Button, Icon, Paper, makeStyles, Theme } from '@material-ui/core';
import { capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { DeviceConfigFirmwareSchema, KeyValuePair, ProjectDevice } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { RegisterDeviceStepProps, useDeviceRegistrationWizardStyle } from '.';
import { DeviceConfigCompProps, DeviceConfigForm } from '../../../components/devices';
import { addDeviceConfigParamSetInputsInstance, DeviceConfigInputErrors, DeviceConfigInputs, updateDeviceConfigInput,
    removeDeviceConfigParamSetInputErrorsInstance, removeDeviceConfigParamSetInputsInstance } from '../../../components/devices/utils';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';
import { clearDeviceConfigInputErrorForParamName, validateDeviceConfigInputs } from '../utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        componentInfo: {
            fontWeight: 'bold',
            fontSize  : 'larger',
            margin    : theme.spacing(0, 0, 2, 0),
        },
    };
});



/**
 * A helper function to replace a deviceConfigInputs for 1 schema in the array of "currentDeviceConfigInputsForAllSchemas" at the specified
 * "schemaIndex". We need to do this a a few places therefore we will create a helper function for it.
 */
const replaceOneDeviceConfigInputs = (
    currentDeviceConfigInputsForAllSchemas: readonly DeviceConfigInputs[],
    newDeviceConfigInputsForOneSchema: DeviceConfigInputs,
    schemaIndex: number,
): readonly DeviceConfigInputs[] => {
    // Return a NEW deviceConfigInputsForAllSchemas by copying the "currentDeviceConfigInputsForAllSchemas" and
    // replace the item at "schemaIndex" with the "newDeviceConfigInputsForOneSchema"
    const newDeviceConfigInputsForAllSchemas: DeviceConfigInputs[] = JSON.parse(JSON.stringify(currentDeviceConfigInputsForAllSchemas));

    newDeviceConfigInputsForAllSchemas[schemaIndex] = newDeviceConfigInputsForOneSchema;

    return newDeviceConfigInputsForAllSchemas;
};



/**
 * A helper function to replace a inputErrors for 1 schema in the array of "currentInputErrorsForAllSchemas" at the specified
 * "schemaIndex". We need to do this a a few places therefore we will create a helper function for it.
 */
const replaceInputErrorsForOneSchema = (
    currentInputErrorsForAllSchemas: readonly DeviceConfigInputErrors[],
    newInputErrorsForOneSchema: DeviceConfigInputErrors,
    schemaIndex: number,
): readonly DeviceConfigInputErrors[] => {
    // Return a newInputErrorsForAllSchemas by copying the "currentInputErrorsForAllSchemas" and
    // replace the item at "schemaIndex" with the "newInputErrorsForOneSchema"
    const newInputErrorsForAllSchemas: DeviceConfigInputErrors[] = JSON.parse(JSON.stringify(currentInputErrorsForAllSchemas));

    newInputErrorsForAllSchemas[schemaIndex] = newInputErrorsForOneSchema;

    return newInputErrorsForAllSchemas;
};



/**
 * The required props for the DeviceRegistrationConfigureDeviceStep in addition to the props inherited from RegisterDeviceStep
 */
export interface DeviceRegistrationConfigureDeviceStepProps extends RegisterDeviceStepProps {
    readonly targetDevice: ProjectDevice;
    readonly allDeviceConfigSchemas: readonly DeviceConfigFirmwareSchema[] | undefined;
    readonly allDDPs: readonly KeyValuePair[] | undefined;
    readonly deviceConfigInputsForAllSchemas: readonly DeviceConfigInputs[] | undefined
    readonly onStepCompleted: (
        deviceConfigInputsForAllSchemas: readonly DeviceConfigInputs[],
    )=> void;
}


/**
 * This is the Configure Device step which is the step which the user needs to fill in the value for the device configuration
 */
export const DeviceRegistrationConfigureDeviceStep: React.FC<DeviceRegistrationConfigureDeviceStepProps> = (
    props: DeviceRegistrationConfigureDeviceStepProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const sharedClasses = useDeviceRegistrationWizardStyle();
    const [inputErrorsForAllSchemas, setInputErrorsForAllSchemas] = useState<readonly DeviceConfigInputErrors[]>([]);
    const { allDeviceConfigSchemas, allDDPs, targetDevice } = props;


    const [deviceConfigInputsForAllSchemas, setDeviceConfigInputsForAllSchemas] = useState<readonly DeviceConfigInputs[] | undefined>(
        props.deviceConfigInputsForAllSchemas,
    );

    useEffect(() => {
        setDeviceConfigInputsForAllSchemas(props.deviceConfigInputsForAllSchemas);
    }, [allDDPs, allDeviceConfigSchemas, props.deviceConfigInputsForAllSchemas, targetDevice.id]);



    /**
     * Submit the user inputs for this step. Before submitting the inputs, we will validate the inputs. If there are no errors,
     * we will notify the parent container by calling ths 'onStepCompleted' callback and provide the parent container with the new
     * inputs and return 'true' for success. If there are errors, we will notify the parent container that there are errors but only
     * if 'dispatchOnError' is true and also return false for failure.
     */
    const submitInputs = useCallback((dispatchOnError: boolean = true): boolean => {

        if (!allDeviceConfigSchemas) {
            return false;
        }


        // Validate inputs
        const newInputErrorsForAllSchemas = allDeviceConfigSchemas.map((schema, schemaIndex) => {
            const inputsForOneSchema = deviceConfigInputsForAllSchemas?.[schemaIndex];
            if (inputsForOneSchema) {
                return validateDeviceConfigInputs(inputsForOneSchema, schema.deviceConfigSchema, true, trans);
            }
            return [];
        });


        // Update the local inputErrors
        setInputErrorsForAllSchemas(newInputErrorsForAllSchemas);

        // Check if there is any non-empty error message in the "newInputErrorsForAllSchemas". If there is at least 1 then we can assume
        // there are errors in the inputs.

        // There are multiple schemas so we need go through the input errors for each schema
        const hasErrors = newInputErrorsForAllSchemas.some((inputErrorsForOneSchema) => {
            // Each schema has multiple param sets so we need to go though the input errors for each param set
            return inputErrorsForOneSchema.some((inputErrorsForOneParamSet) => {
                // Each param set has multiple instances so we need to go through the input errors for each instance
                return inputErrorsForOneParamSet.some((inputErrorsForOneParamSetInstance) => {
                    // Each instance has a map of paramName => errorMessage so we need to go through each param and find one that has non-empty error
                    return Object.values(inputErrorsForOneParamSetInstance).some((errorMessage) => {
                        return errorMessage !== undefined;
                    });
                });
            });
        });


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        // If there are no errors, notify the parent container this step is completed
        if (!hasErrors && deviceConfigInputsForAllSchemas) {
            props.onStepCompleted(deviceConfigInputsForAllSchemas);
        }

        return !hasErrors;

    }, [deviceConfigInputsForAllSchemas, allDeviceConfigSchemas, props, trans]);



    /**
     * When one of the param input changed, we will update the deviceConfigInputsForAllSchemas state
     */
    const onDeviceInputChangeHandler = useCallback((
        inputName: string, value: any, paramSetIndex: number, paramSetInstanceIndex: number, schemaIndex: number,
    ) => {
        // Update the current deviceConfigInputs
        setDeviceConfigInputsForAllSchemas((currentDeviceConfigInputsForAllSchemas) => {
            const deviceConfigInputsForOneSchema = currentDeviceConfigInputsForAllSchemas?.[schemaIndex];
            if (currentDeviceConfigInputsForAllSchemas && deviceConfigInputsForOneSchema) {
                const newDeviceConfigInputsForOneSchema = updateDeviceConfigInput(
                    inputName, value, paramSetIndex, paramSetInstanceIndex, deviceConfigInputsForOneSchema,
                );

                // Because the input for a param changed, we need to clear the error for that param.
                setInputErrorsForAllSchemas((currentInputErrorsForAllSchemas) => {
                    const inputErrorsForOneSchema = currentInputErrorsForAllSchemas[schemaIndex];
                    const newInputErrorsForOneSchema = clearDeviceConfigInputErrorForParamName(
                        inputErrorsForOneSchema, inputName, paramSetIndex, paramSetInstanceIndex,
                    );

                    return replaceInputErrorsForOneSchema(
                        currentInputErrorsForAllSchemas, newInputErrorsForOneSchema, schemaIndex,
                    );
                });

                // The input for this step is modified so we can clear the error for this step
                props.onStepError(false);

                // Now replace the old deviceConfigInputsForOneSchema with the newDeviceConfigInputsForOneSchema
                return replaceOneDeviceConfigInputs(currentDeviceConfigInputsForAllSchemas, newDeviceConfigInputsForOneSchema, schemaIndex);
            }
            return currentDeviceConfigInputsForAllSchemas;
        });
    }, [props]);


    /**
     * Create and add a new paramSet inputs instance for a paramSet
     */
    const onAddParamInstanceClicks = useCallback((paramSetIndex: number, schemaIndex: number) => {
        setDeviceConfigInputsForAllSchemas((currentDeviceConfigInputsForAllSchemas) => {
            const deviceConfigInputsForOneSchema = currentDeviceConfigInputsForAllSchemas?.[schemaIndex];
            const deviceConfigSchema = allDeviceConfigSchemas?.[schemaIndex];

            if (currentDeviceConfigInputsForAllSchemas && deviceConfigInputsForOneSchema && deviceConfigSchema) {
                const newDeviceConfigInputsForOneSchema = addDeviceConfigParamSetInputsInstance(
                    targetDevice.id, paramSetIndex, deviceConfigInputsForOneSchema, deviceConfigSchema.deviceConfigSchema,
                );
                // Now replace the old deviceConfigInputsForOneSchema with the newDeviceConfigInputsForOneSchema
                if (newDeviceConfigInputsForOneSchema) {
                    return replaceOneDeviceConfigInputs(currentDeviceConfigInputsForAllSchemas, newDeviceConfigInputsForOneSchema, schemaIndex);
                }
            }

            return currentDeviceConfigInputsForAllSchemas;
        });
    }, [allDeviceConfigSchemas, targetDevice.id]);


    /**
     * Remove a paramSet inputs instance at the given paramSetIndex and paramSetInstanceIndex
     */
    const onRemoveParamInstanceClicks = useCallback((paramSetIndex: number, paramSetInstanceIndex: number, schemaIndex: number) => {
        setDeviceConfigInputsForAllSchemas((currentDeviceConfigInputsForAllSchemas) => {
            const deviceConfigInputsForOneSchema = currentDeviceConfigInputsForAllSchemas?.[schemaIndex];

            if (currentDeviceConfigInputsForAllSchemas && deviceConfigInputsForOneSchema) {
                const newDeviceConfigInputsForOneSchema = removeDeviceConfigParamSetInputsInstance(
                    paramSetIndex, paramSetInstanceIndex, deviceConfigInputsForOneSchema,
                );

                // Now replace the old deviceConfigInputsForOneSchema with the newDeviceConfigInputsForOneSchema
                if (newDeviceConfigInputsForOneSchema) {

                    // Because we removed config for 1 param set instance from the data, we need to remove the input errors as well
                    setInputErrorsForAllSchemas((currentInputErrorsForAllSchemas) => {
                        const inputErrorsForOneSchema = currentInputErrorsForAllSchemas[schemaIndex];
                        const newInputErrorsForOneSchema = removeDeviceConfigParamSetInputErrorsInstance(
                            paramSetIndex, paramSetInstanceIndex, inputErrorsForOneSchema,
                        );

                        if (newInputErrorsForOneSchema) {
                            return replaceInputErrorsForOneSchema(
                                currentInputErrorsForAllSchemas, newInputErrorsForOneSchema, schemaIndex,
                            );
                        }

                        // There is no error for the specified schema so just return the currentInputErrorsForAllSchemas
                        return currentInputErrorsForAllSchemas;
                    });

                    // Now replace the old deviceConfigInputsForOneSchema with the newDeviceConfigInputsForOneSchema
                    return replaceOneDeviceConfigInputs(currentDeviceConfigInputsForAllSchemas, newDeviceConfigInputsForOneSchema, schemaIndex);
                }
            }

            return currentDeviceConfigInputsForAllSchemas;
        });
    }, []);


    /**
     * Create and cache the text used by the "DeviceConfigForm"
     */
    const deviceConfigFormTexts = useMemo((): DeviceConfigCompProps['texts'] => {
        return {
            emptyRepeatableParamSetInputsMessage: trans('device_config.empty_repeatable_param_set_inputs.message'),
            add                                 : trans('label.add'),
        };
    }, [trans]);


    /**
     * Check if there are device config schema. Just because "configSchemas" is not undefined DOES NOT mean the device has device config schema
     * configured. It is possible that ALL "schema.deviceConfigSchema" in the "configSchemas" are undefined or empty arrays. Therefore, we need
     * to loop through all schema in the "configSchemas" and make sure at least 1 of them has "deviceConfigSchema" that is no undefined and length
     * greater than 0.
     */
    const hasDeviceConfigSchemas = useMemo(() => {
        return allDeviceConfigSchemas?.some((schema) => {
            return schema.deviceConfigSchema && schema.deviceConfigSchema.length > 0;
        });
    }, [allDeviceConfigSchemas]);



    return (
        <div className={sharedClasses.stepContentBody}>
            <div className={clsx(sharedClasses.stepContentForm)}>
                <div className={clsx(sharedClasses.stepSubtitle)}>
                    {trans('device_registration_wizard.step_configure_device.subtitle')}
                </div>
                {hasDeviceConfigSchemas && deviceConfigInputsForAllSchemas && allDeviceConfigSchemas ? (
                    <>
                        {allDeviceConfigSchemas.map((schema, schemaIndex) => {
                            if (schema.deviceConfigSchema && schema.deviceConfigSchema.length > 0) {
                                const deviceConfigObject = deviceConfigInputsForAllSchemas[schemaIndex];
                                const oneInputErrors = inputErrorsForAllSchemas[schemaIndex];
                                return (
                                    <Paper
                                        key={`${schema.componentName}-${schema.componentVersionName}`}
                                        elevation={1}
                                        className={clsx(sharedClasses.formControlGroup)}
                                    >
                                        <div className={classes.componentInfo}>
                                            {schema.componentName} {schema.componentVersionName}
                                        </div>
                                        {schema.deviceConfigSchema && schema.deviceConfigSchema.length > 0 && deviceConfigObject ? (
                                            <DeviceConfigForm
                                                isEditing={true}
                                                deviceId={targetDevice.id}
                                                deviceConfigSchema={schema.deviceConfigSchema}
                                                deviceConfigInputs={deviceConfigObject}
                                                inputErrors={oneInputErrors}
                                                texts={deviceConfigFormTexts}
                                                onInputChange={(inputName: string, value: any, paramSetIndex: number, instanceIndex: number) => {
                                                    onDeviceInputChangeHandler(inputName, value, paramSetIndex, instanceIndex, schemaIndex);
                                                }}
                                                onAddParamInstanceClicks={(paramSetIndex: number) => {
                                                    onAddParamInstanceClicks(paramSetIndex, schemaIndex);
                                                }}
                                                onRemoveParamInstanceClicks={(paramSetIndex: number, paramSetInstanceIndex: number) => {
                                                    onRemoveParamInstanceClicks(paramSetIndex, paramSetInstanceIndex, schemaIndex);
                                                }}
                                            />
                                        ) : (
                                            <Fragment key={`${schema.componentName}-${schema.componentVersionName}`} />
                                        )}
                                    </Paper>
                                );
                            }
                            return (
                                <Fragment
                                    key={`${schema.componentName}-${schema.componentVersionName}`}
                                />
                            );
                        })}
                    </>
                ) : (
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.device_config_schema', { count: 2 })),
                        })}
                    />
                )}
            </div>

            <div className={sharedClasses.stepContentActionsContainer}>
                <div className={sharedClasses.stepContentRightActionsContainer}>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            props.onStepError(false);
                            submitInputs(false);
                            props.onPrevStepClick();
                        }}
                        className={sharedClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        onClick={() => {
                            if (submitInputs()) {
                                props.onNextStepClick();
                            }
                        }}
                        className={sharedClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                    </Button>
                </div>
            </div>
        </div>
    );
};
